import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Eye, Mail, CheckCircle, AlertCircle, Loader, Users, BarChart3, Monitor, Smartphone, X, Sun, Moon } from 'lucide-react';
import { blogPosts } from '@/data/blogPosts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface Campaign {
  id: number;
  name: string;
  subject: string;
  status: string;
  created_at: string;
  sent_date?: string;
}

interface CampaignStats {
  name: string;
  subject: string;
  status: string;
  sent_date?: string;
  statistics: {
    unique_clicks: number;
    clicks: number;
    unique_views: number;
    delivered: number;
    sent: number;
    hard_bounces: number;
    soft_bounces: number;
    unsubscribed: number;
  };
}

const AdminBlogCampaign: React.FC = () => {
  const [selectedBlog, setSelectedBlog] = useState<string>('');
  const [selectedLists, setSelectedLists] = useState<string[]>(['general']);
  const [customSubject, setCustomSubject] = useState('');
  const [testEmails, setTestEmails] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignStats, setSelectedCampaignStats] = useState<CampaignStats | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const listOptions = [
    { key: 'general', label: 'General Security', description: 'Main newsletter list' },
    { key: 'application_security', label: 'Application Security', description: 'App security focused' },
    { key: 'devsecops', label: 'DevSecOps', description: 'DevOps security automation' },
    { key: 'offensive_security', label: 'Offensive Security', description: 'Penetration testing & red team' },
    { key: 'cloud_security', label: 'Cloud Security', description: 'AWS/Azure/GCP security' },
    { key: 'advisory_services', label: 'Advisory Services', description: 'Security consulting' }
  ];

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await fetch('/api/campaigns?limit=10');
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const handleListToggle = (listKey: string) => {
    setSelectedLists(prev => 
      prev.includes(listKey)
        ? prev.filter(key => key !== listKey)
        : [...prev, listKey]
    );
  };

  const handlePreview = async () => {
    const selectedBlogData = blogPosts.find(post => post.slug === selectedBlog);
    if (!selectedBlogData) {
      setStatus('error');
      setMessage('Please select a blog post first');
      return;
    }

    setIsGeneratingPreview(true);
    setStatus('idle');
    try {
      const response = await fetch('/api/blog/preview-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blog_data: selectedBlogData })
      });

      const data = await response.json();
      if (data.success) {
        setPreviewHtml(data.html_content);
        setShowPreview(true);
        // Write content to iframe after modal opens
        setTimeout(() => {
          updateIframeContent(data.html_content);
        }, 100);
      } else {
        setStatus('error');
        setMessage(data.error || 'Preview failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to generate preview');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const updateIframeContent = (htmlContent: string) => {
    if (iframeRef.current && iframeRef.current.contentDocument) {
      const doc = iframeRef.current.contentDocument;
      
      // Apply theme styles and responsive viewport
      let themedContent = htmlContent;
      
      // Add responsive viewport meta tag and device-specific styles
      const viewportStyles = previewDevice === 'mobile' ? `
        <style>
          @media screen {
            body { 
              width: 100% !important;
              min-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .email-container {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
            }
            table[class="email-container"] {
              width: 100% !important;
              max-width: 100% !important;
            }
          }
        </style>
      ` : '';
      
      if (previewTheme === 'dark') {
        themedContent = applyDarkThemeToEmail(htmlContent);
      } else {
        // Ensure clean white background for light theme
        themedContent = htmlContent.replace(
          '</head>',
          '<style>html, body { background-color: #ffffff !important; }</style></head>'
        );
      }
      
      // Add viewport styles
      themedContent = themedContent.replace('</head>', viewportStyles + '</head>');
      
      doc.open();
      doc.write(themedContent);
      doc.close();
      
      // Force iframe to use mobile viewport width
      if (previewDevice === 'mobile' && doc.documentElement) {
        doc.documentElement.style.width = '100%';
        doc.body.style.width = '100%';
      }
    }
  };

  const applyDarkThemeToEmail = (html: string) => {
    // Inject dark theme CSS overrides
    const darkThemeStyles = `
      <style>
        /* Dark theme overrides */
        html {
          background-color: #0a0a0a !important;
        }
        body { 
          background-color: #0a0a0a !important; 
          color: #e0e0e0 !important;
        }
        .email-container { 
          background-color: #0a0a0a !important;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
        }
        .header {
          background: linear-gradient(135deg, #00a86b 0%, #06b6d4 100%) !important;
        }
        .blog-header {
          background-color: #0a0a0a !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
        }
        .blog-title {
          color: #ffffff !important;
        }
        .blog-meta {
          color: #a0a0a0 !important;
        }
        .content {
          background-color: #0a0a0a !important;
          color: #e0e0e0 !important;
        }
        .content p {
          color: #e0e0e0 !important;
        }
        .content h2 {
          color: #ffffff !important;
          border-bottom-color: #00a86b !important;
        }
        .content h3 {
          color: #ffffff !important;
        }
        .quote {
          background: #1a1a2e !important;
          border-left-color: #00a86b !important;
          color: #b0b0b0 !important;
        }
        .footer {
          background: #000000 !important;
          border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
        }
        .footer a {
          color: #06b6d4 !important;
        }
        /* List items */
        ul li, ol li {
          color: #e0e0e0 !important;
        }
        /* Links in content */
        .content a {
          color: #06b6d4 !important;
        }
        /* Code blocks */
        code, pre {
          background-color: #1a1a2e !important;
          color: #00a86b !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
        }
      </style>
    `;
    
    // Insert the dark theme styles before the closing </head> tag
    return html.replace('</head>', darkThemeStyles + '</head>');
  };

  // Update iframe content when device or theme changes
  useEffect(() => {
    if (showPreview && previewHtml) {
      updateIframeContent(previewHtml);
    }
  }, [previewDevice, previewTheme, showPreview]);

  const handleSendTest = async () => {
    const selectedBlogData = blogPosts.find(post => post.slug === selectedBlog);
    if (!selectedBlogData) {
      setStatus('error');
      setMessage('Please select a blog post first');
      return;
    }

    if (!testEmails.trim()) {
      setStatus('error');
      setMessage('Please enter test email addresses');
      return;
    }

    const emailList = testEmails.split(',').map(email => email.trim()).filter(email => email);
    
    setIsLoading(true);
    setStatus('idle');

    try {
      const response = await fetch('/api/blog/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blog_data: selectedBlogData,
          test_emails: emailList,
          subject: customSubject || undefined
        })
      });

      const data = await response.json();
      if (data.success) {
        setStatus('success');
        setMessage(`Test email sent to ${emailList.length} recipients`);
      } else {
        setStatus('error');
        setMessage(data.error || 'Test email failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to send test email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    const selectedBlogData = blogPosts.find(post => post.slug === selectedBlog);
    if (!selectedBlogData) {
      setStatus('error');
      setMessage('Please select a blog post first');
      return;
    }

    if (selectedLists.length === 0) {
      setStatus('error');
      setMessage('Please select at least one mailing list');
      return;
    }

    setIsLoading(true);
    setStatus('idle');

    try {
      const response = await fetch('/api/blog/send-campaign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blog_data: selectedBlogData,
          target_lists: selectedLists,
          subject: customSubject || undefined,
          send_immediately: true
        })
      });

      const data = await response.json();
      if (data.success) {
        setStatus('success');
        setMessage(`Campaign sent successfully! Campaign ID: ${data.campaign_id}`);
        loadCampaigns(); // Refresh campaigns list
      } else {
        setStatus('error');
        setMessage(data.error || 'Campaign failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to send campaign');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetStats = async (campaignId: number) => {
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/stats`);
      const data = await response.json();
      if (data.success) {
        setSelectedCampaignStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading campaign stats:', error);
    }
  };

  return (
    <div className="admin-blog-campaign">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-white mb-1">
            Blog Campaign Manager
          </h1>
          <p className="text-sm text-gray-400">
            Send blog posts as email newsletters to your subscribers
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Campaign Creation */}
          <div className="bg-[#0a0a0a] backdrop-blur-sm rounded-xl p-6 border border-white/[0.08]">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Send className="w-4 h-4 mr-2" />
              Create Campaign
            </h2>

            {/* Blog Post Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select Blog Post
              </label>
              <select
                value={selectedBlog}
                onChange={(e) => setSelectedBlog(e.target.value)}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15]"
              >
                <option value="">Choose a blog post...</option>
                {blogPosts.map(post => (
                  <option key={post.slug} value={post.slug} className="bg-[#0a0a0a] text-white">
                    {post.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Subject */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Custom Subject (optional)
              </label>
              <input
                type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Leave empty for auto-generated subject"
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15]"
              />
            </div>

            {/* List Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Target Lists
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {listOptions.map(option => (
                  <label key={option.key} className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedLists.includes(option.key)}
                      onChange={() => handleListToggle(option.key)}
                      className="mt-1 mr-3 accent-[#00a86b]"
                    />
                    <div>
                      <span className="text-white text-sm">{option.label}</span>
                      <p className="text-gray-400 text-xs">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Selected: {selectedLists.length} list{selectedLists.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Test Emails */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Test Email Addresses
              </label>
              <textarea
                value={testEmails}
                onChange={(e) => setTestEmails(e.target.value)}
                placeholder="admin@ciphersecurity.io, test@example.com"
                rows={2}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15] resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                Comma-separated email addresses for testing
              </p>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <button
                onClick={handlePreview}
                disabled={!selectedBlog || isGeneratingPreview}
                className="flex items-center justify-center py-2 px-4 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg transition-colors disabled:opacity-50 border border-white/[0.08]"
              >
                {isGeneratingPreview ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview
              </button>
              
              <button
                onClick={handleSendTest}
                disabled={!selectedBlog || !testEmails.trim() || isLoading}
                className="flex items-center justify-center py-2 px-4 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg transition-colors disabled:opacity-50 border border-white/[0.08]"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Test
              </button>
              
              <button
                onClick={handleSendCampaign}
                disabled={!selectedBlog || selectedLists.length === 0 || isLoading}
                className="flex items-center justify-center py-2 px-4 bg-[#00a86b]/20 hover:bg-[#00a86b]/30 text-[#00a86b] rounded-lg transition-colors disabled:opacity-50 border border-[#00a86b]/40"
              >
                {isLoading ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Campaign
              </button>
            </div>

            {/* Status Messages */}
            <AnimatePresence mode="wait">
              {status === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center p-3 bg-green-500/20 border border-green-500/40 rounded-lg text-green-200 text-sm"
                >
                  <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{message}</span>
                </motion.div>
              )}
              
              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center p-3 bg-red-500/20 border border-red-500/40 rounded-lg text-red-200 text-sm"
                >
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{message}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Campaign History & Stats */}
          <div className="bg-[#0a0a0a] backdrop-blur-sm rounded-xl p-6 border border-white/[0.08] flex flex-col">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-4 h-4 mr-2" />
              Recent Campaigns
            </h2>

            <div className="space-y-3 flex-1 overflow-y-auto">
              {campaigns.length === 0 ? (
                <p className="text-gray-400 text-sm">No campaigns found</p>
              ) : (
                campaigns.map(campaign => (
                  <div key={campaign.id} className="bg-[#0a0a0a] rounded-lg p-4 border border-white/[0.08]">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-white font-medium text-sm">{campaign.name}</h3>
                      <span className={`px-2 py-1 rounded text-xs ${
                        campaign.status === 'sent' ? 'bg-green-500/20 text-green-200' :
                        campaign.status === 'draft' ? 'bg-gray-500/20 text-gray-200' :
                        'bg-yellow-500/20 text-yellow-200'
                      }`}>
                        {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-gray-300 text-sm mb-2">{campaign.subject}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-xs">
                        {campaign.sent_date || campaign.created_at}
                      </span>
                      <button
                        onClick={() => handleGetStats(campaign.id)}
                        className="text-[#06b6d4] hover:text-[#0891b2] text-xs"
                      >
                        View Stats
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Campaign Stats Modal */}
            {selectedCampaignStats && (
              <div className="mt-6 bg-[#0a0a0a] rounded-lg p-4 border border-white/[0.08]">
                <h3 className="text-white font-medium mb-3">Campaign Statistics</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400">Sent:</span>
                    <span className="text-white ml-2">{selectedCampaignStats.statistics.sent}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Delivered:</span>
                    <span className="text-white ml-2">{selectedCampaignStats.statistics.delivered}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Opened:</span>
                    <span className="text-white ml-2">{selectedCampaignStats.statistics.unique_views}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Clicked:</span>
                    <span className="text-white ml-2">{selectedCampaignStats.statistics.unique_clicks}</span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCampaignStats(null)}
                  className="mt-3 text-[#06b6d4] hover:text-[#0891b2] text-xs"
                >
                  Close Stats
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Email Preview Modal */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] w-full bg-[#0a0a0a] border border-white/[0.08] p-0 overflow-hidden">
            <DialogHeader className="px-6 py-4 border-b border-white/[0.08]">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-white text-lg font-semibold">Email Preview</DialogTitle>
                  <DialogDescription className="text-gray-400 text-sm mt-1">
                    {customSubject || blogPosts.find(p => p.slug === selectedBlog)?.title || 'Preview how your email will appear to recipients'}
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Theme Toggle */}
                  <div className="flex bg-[#0a0a0a] rounded-lg border border-white/[0.08] p-1">
                    <button
                      onClick={() => setPreviewTheme('light')}
                      className={`p-2 rounded transition-colors ${
                        previewTheme === 'light'
                          ? 'bg-[#00a86b]/20 text-[#00a86b]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      title="Light Theme"
                    >
                      <Sun className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPreviewTheme('dark')}
                      className={`p-2 rounded transition-colors ${
                        previewTheme === 'dark'
                          ? 'bg-[#00a86b]/20 text-[#00a86b]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      title="Dark Theme"
                    >
                      <Moon className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Device Toggle */}
                  <div className="flex bg-[#0a0a0a] rounded-lg border border-white/[0.08] p-1">
                    <button
                      onClick={() => setPreviewDevice('desktop')}
                      className={`p-2 rounded transition-colors ${
                        previewDevice === 'desktop'
                          ? 'bg-[#00a86b]/20 text-[#00a86b]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      title="Desktop View"
                    >
                      <Monitor className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPreviewDevice('mobile')}
                      className={`p-2 rounded transition-colors ${
                        previewDevice === 'mobile'
                          ? 'bg-[#00a86b]/20 text-[#00a86b]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      title="Mobile View"
                    >
                      <Smartphone className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </DialogHeader>
            
            {/* Preview Container */}
            <div className={`relative p-4 overflow-auto transition-colors duration-300 ${
              previewTheme === 'dark' ? 'bg-[#0a0a0a]' : 'bg-gray-100'
            }`} style={{ height: 'calc(90vh - 120px)' }}>
              <div className="flex justify-center">
                <div className={`transition-all duration-300 rounded-lg overflow-hidden border ${
                  previewDevice === 'mobile' ? 'w-[375px]' : 'w-full max-w-[800px]'
                } ${
                  previewTheme === 'dark' 
                    ? 'bg-[#1a1a2e] shadow-2xl shadow-black/50 border-white/20' 
                    : 'bg-white shadow-xl border-white/30'
                }`}>
                  {/* Email Client Header Mock */}
                  <div className={`px-4 py-3 transition-colors duration-300 ${
                    previewTheme === 'dark'
                      ? 'bg-[#0a0a0a]'
                      : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>From:</span>
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>CIPHER Security &lt;info@ciphersecurity.io&gt;</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>To:</span>
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>subscriber@example.com</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>Subject:</span>
                      <span className={`text-xs font-medium ${
                        previewTheme === 'dark' ? 'text-gray-100' : 'text-gray-700'
                      }`}>
                        {customSubject || `🛡️ ${blogPosts.find(p => p.slug === selectedBlog)?.title || 'Newsletter'}`}
                      </span>
                    </div>
                  </div>
                  
                  {/* Separator/Gap */}
                  <div className={`h-3 transition-colors duration-300 ${
                    previewTheme === 'dark'
                      ? 'bg-[#0a0a0a] border-t border-white/[0.08]'
                      : 'bg-gray-50 border-t border-gray-200'
                  }`} />
                  
                  {/* Email Content Iframe */}
                  <iframe
                    ref={iframeRef}
                    className="w-full border-0"
                    style={{ 
                      height: previewDevice === 'mobile' ? '600px' : '800px',
                      backgroundColor: previewTheme === 'dark' ? '#0a0a0a' : '#ffffff',
                      colorScheme: previewTheme === 'dark' ? 'dark' : 'light',
                      width: '100%',
                      display: 'block'
                    }}
                    title="Email Preview"
                    sandbox="allow-same-origin allow-scripts"
                  />
                </div>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="px-6 py-3 border-t border-white/[0.08] bg-[#0a0a0a] flex justify-between items-center">
              <p className="text-xs text-gray-400">
                {previewTheme === 'dark' ? 'Dark theme (if supported by email client)' : 'Light theme (default)'} • {previewDevice === 'mobile' ? 'Mobile' : 'Desktop'} view
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSendTest()}
                  disabled={!testEmails.trim() || isLoading}
                  className="px-4 py-2 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg text-sm transition-colors disabled:opacity-50 border border-white/[0.08]"
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  Send Test
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminBlogCampaign;